import {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  type RefObject,
} from "react";
import "@css/temp_create.css";
import type * as type from "@vTypes";
import useRosterConfig from "@/hooks/useRosterConfig";
import TableBody from "@components/feature/rosterTable/TableBody.tsx";
import ButtonGroup from "@components/feature/roster_create/ButtonGroup.tsx";
import ModalRosterModify from "@components/feature/roster_create/ModalRosterModify.tsx";
import SetDeadLine from "@components/feature/roster_create/SetDeadLine.tsx";
import LoadingDots from "@components/LoadingDots.tsx";
import ModalManpower from "@components/feature/roster_create/ModalManpower.tsx";
import Roster_create_Tab2 from "@components/feature/rosterTable/Roster_create_Tab2.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useInitializeGroup } from "@hooks/useInitializeGroup.ts";
import { useGroupStore } from "@store/groupStore.ts";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth.ts";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import RosterMadeTable from "@components/feature/rosterTable/RosterMadeTable.tsx";
import { RosterViolationFloatingControls } from "@/components/feature/rosterTable/RosterViolationFloatingControls";
import NurseEditSideProfile from "@components/feature/head_nurse_mgmt/NurseEditSideProfile.tsx";
import ModalOffRequestList from "@components/feature/roster_create/components/ModalOffRequestList.tsx";
import ContainerLoadingOverlay from "@components/modal/ContainerLoadingOverlay";
import { useRosterResources } from "@hooks/useRosterMetaresource.ts";
import { useRosterInitializer } from "@hooks/useRosterInitalizer.ts";
import { useScheduleStore } from "@store/scheduleStore";
import useRosterVersions from "@/hooks/roster-craete/useRosterVersions";
import useRosterSchedule from "@/hooks/roster-craete/useRosterSchedule";
import useGroupMembers from "@/hooks/useGroupMembers";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import { useHolidayDatesQuery } from "@/hooks/useHolidayDatesQuery";
import useRosterStatus from "@/hooks/zustand/useRosterStatus";
import RosterIndicater from "./components/RosterIndicater";
import RosterViewSettings from "./components/RosterViewSettings";
import { cn } from "@/lib/utils";
import type { NurseResponse } from "@/types/nurse-type";
import ModalRosterWanted from "@/components/feature/roster_create/modal-roster-wanted";
import ModalWantedConfig from "@/components/feature/roster_create/modal-wanted-config";
import useGenerateRosterMutation, {
  type GenerateRosterResponse,
  RosterInfeasibleError,
  type Infeasibility,
  type ResolutionOption,
} from "./hook/useGenerateRosterMutation";
import type { RosterCreateConfigPayload } from "./service/rosterCreateConfigPayload";
import type { FullNurseSchemaWithPreceptor } from "@/utils/rosterCreate-util";
import { useWantedAdjustmentQuery } from "@/hooks/roster-craete/useWantedAdjustmentQuery";
import useStore from "@/store/store";
import { useRosterValidation } from "@/hooks/useRosterValidation";
import { getPrecepteeDaysByNurseId } from "@/utils/precepteePolicy";
import { usePrecepteePeriodsQuery } from "@/hooks/roster-craete/usePrecepteePeriodsQuery";
import useRosterHistory from "./hook/useRosterHistory";
import useManpower from "./hook/useManpower";
import useRosterEditing from "./hook/useRosterEditing";
import { ModifiedCellsProvider } from "./context/ModifiedCellsContext";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import { useEmergencyReplacement } from "./hook/useEmergencyReplacement";
import { useRosterPublicationControls } from "./hook/useRosterPublicationControls";
import { useRosterUseMidControl } from "./hook/useRosterUseMidControl";
import { useRosterPrevTailPanel } from "./hook/useRosterPrevTailPanel";
import {
  useRosterWantedOverlay,
  useWantedComplianceTotals,
} from "./hook/useRosterWantedOverlay";
import { useNurseAssignmentsDebugLogger } from "./hook/useNurseAssignmentsDebugLogger";
import { useRosterExcelDownload } from "./hook/useRosterExcelDownload";
import { useRosterCreateJobEffects } from "./hook/useRosterCreateJobEffects";
import { useRosterCreateResourceSync } from "./hook/useRosterCreateResourceSync";
import { useRosterVersionSelectionEffects } from "./hook/useRosterVersionSelectionEffects";
import { useRosterCreateDerivedNurses } from "./hook/useRosterCreateDerivedNurses";
import { useRosterCreateDisplayShifts } from "./hook/useRosterCreateDisplayShifts";
import { useRosterCreateNurseReorder } from "./hook/useRosterCreateNurseReorder";
import { useRosterCreateVisibleNurses } from "./hook/useRosterCreateVisibleNurses";
import { RosterCreateTableHeader } from "./components/RosterCreateTableHeader";
import { RosterCreateBlockingInfeasibilityAlert } from "./components/RosterCreateBlockingInfeasibilityAlert";
import type { GroupMemberInMonth } from "@/api/nurses";
import { toast } from "@/components/toast/CustomToast";

// 이니셜 데이터 or CopyNurses 타입 (정의는 rosterCreate-util)
export type { CopyNurses } from "@utils/rosterCreate-util.ts";
export type FullNurseSchemaAddPreceptor = FullNurseSchemaWithPreceptor[];

// 최대위반 설정 on OFF
const ENABLE_MAX_MANPOWER_VIOLATION = false;
const SHOULD_USE_ASYNC_ROSTER_GENERATION =
  import.meta.env.VITE_ENV !== "LOCAL";
const PRECEPTEE_STATS_INCLUDED_STORAGE_KEY =
  "roster_create_preceptee_stats_included";
const MOCK_INFEASIBILITY_FLOW_STORAGE_KEY = "roster_create_mock_infeasible";
const PRECEPTOR_MARK_COLORS = [
  "254, 14, 130",
  "14, 89, 198",
  "41, 171, 226",
  "247, 147, 30",
] as const;

const MOCK_BLOCKING_INFEASIBILITY = {
  severity: "blocking",
  ui_text: {
    title: "근무표 생성 조건을 조정해야 합니다",
    description:
      "현재 조건에서는 생성이 어렵습니다. 해결 방법을 선택하면 조정된 조건으로 다시 생성합니다.",
    options_header: "아래 {n}가지 중에 선택하여 조정할 수 있습니다.",
    apply_button: "이 방법으로 다시 생성",
  },
  summary_message_ko:
    "팀별 최소 인원 합계가 해당 시프트의 일별 요구 인원을 초과합니다.",
  preflight_issues: [
    {
      reason_code: "team_min_exceeds_daily_requirement",
      severity: "blocking",
      evidence: {
        shift: "D",
        day: 2,
        required: 3,
        team_min_total: 4,
      },
      human_message_ko:
        "팀별 최소 인원 합계가 주간(D) 2일차 요구 인원을 초과합니다.",
      fix_suggestions_ko: [
        "팀별 최소 인원을 낮추세요.",
        "일별 요구 인원을 늘리세요.",
      ],
    },
    {
      reason_code: "night_month_limit_too_low",
      severity: "blocking",
      evidence: {
        nurse_id: "mock-nurse-001",
        current_limit: 2,
        suggested_limit: 3,
      },
      human_message_ko:
        "일부 근무자의 월 나이트 상한이 현재 요구량보다 낮습니다.",
      fix_suggestions_ko: ["월 나이트 상한을 1회 늘려 다시 생성하세요."],
    },
  ],
  applied_relaxations: [],
  fix_suggestions_ko: [
    "팀별 최소 인원을 낮추세요.",
    "월 나이트 상한을 완화하세요.",
  ],
  resolution_options: [
    {
      option_id: "mock-relax-team-min",
      kind: "config_override",
      source: "probe",
      verified: true,
      title_ko: "팀 최소 인원 완화",
      trade_off_ko: "팀별 최소 인원을 1명 낮춰 생성 가능성을 확인합니다.",
      changes: [
        {
          config_key: "team_min_count",
          label_ko: "팀 최소 인원",
          from: 2,
          to: 1,
        },
      ],
      apply: {
        team_min_count: 1,
      },
      fix: {
        mode: "auto_apply",
        where: "roster_config.team_min_count",
        where_label_ko: "근무표 만들기 설정 > 근무 제한 > 팀 최소 인원",
        how_ko: "팀별 최소 인원을 1명 낮춰 다시 생성합니다.",
        config_key: "team_min_count",
      },
    },
    {
      option_id: "mock-raise-night-limit",
      kind: "config_override",
      source: "probe",
      verified: true,
      title_ko: "월 나이트 상한 완화",
      trade_off_ko: "대상 근무자의 월 나이트 상한을 1회 늘립니다.",
      changes: [
        {
          config_key: "monthly_night_limit",
          label_ko: "월 나이트 상한",
          from: 2,
          to: 3,
        },
      ],
      monthly_limit_release: [
        {
          nurse_id: "mock-nurse-001",
          field: "n_max",
          value: 3,
        },
      ],
      fix: {
        mode: "auto_apply",
        where: "nurse_monthly_limit.n_max",
        where_label_ko: "근무표 만들기 설정 > 근무 설정 > 월 나이트 상한",
        how_ko: "월 나이트 상한을 1회 늘려 다시 생성합니다.",
      },
    },
  ],
  causes: [
    {
      reason_code: "team_min_exceeds_daily_requirement",
      human_message_ko:
        "팀별 최소 인원 합계가 주간(D) 2일차 요구 인원을 초과합니다.",
      fix: {
        mode: "manual_edit",
        where: "roster_config.team_min_count",
        where_label_ko: "근무표 만들기 설정 > 근무 제한 > 팀 최소 인원",
      },
    },
  ],
  violation_summary: {
    team_min: {
      count: 1,
      samples: [
        {
          node_id: "mock-day-2-D",
          details: {
            shift: "D",
            day: 2,
          },
          slack: 1,
        },
      ],
    },
  },
  last_error_reason: "mock_infeasible_flow",
} satisfies Infeasibility;

function readPrecepteeStatsIncludedPreference(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.localStorage.getItem(PRECEPTEE_STATS_INCLUDED_STORAGE_KEY) === "true"
  );
}

function shouldShowMockInfeasibilityFlow(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  return (
    params.get("mock_infeasible") === "1" ||
    window.localStorage.getItem(MOCK_INFEASIBILITY_FLOW_STORAGE_KEY) === "1"
  );
}

function Roster_create() {
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const queryClient = useQueryClient();
  const { curDate, curScheduleInfo, setCurScheduleInfo } = useScheduleStore();

  const wantedButtonRef = useRef<HTMLButtonElement>(null);
  const rosterModifyButtonRef = useRef<HTMLButtonElement>(null);
  const [precepteeStatsIncluded, setPrecepteeStatsIncluded] = useState(
    readPrecepteeStatsIncludedPreference
  );

  useEffect(() => {
    window.localStorage.setItem(
      PRECEPTEE_STATS_INCLUDED_STORAGE_KEY,
      precepteeStatsIncluded ? "true" : "false"
    );
  }, [precepteeStatsIncluded]);

  // pooling 상태 받기 위한 job 생성
  const { rosterMade, rosterInfeasibility, setRosterInfeasibility } =
    useRosterStatus();

  // Group Selecter (변경은 useInitializeGroup 등에서 처리)
  const { selectedGroupId, selectedOfficeId } = useGroupStore();

  // 선택 병동은 ADM/비-ADM 공통으로 selectedGroupId 가 권위 소스다.
  // (useInitializeGroup 이 비-ADM 도 loginUser.group_id 로 채운다.)
  // isAdmin 게이팅으로 비우면 비-ADM HN 의 관리병동 버전/그리드가 home 으로 떨어져 빈 화면이 된다.
  const gid = selectedGroupId ?? "";

  useNurseAssignmentsDebugLogger({
    groupId: isAdmin
      ? (selectedGroupId ?? null)
      : (loginUser?.group_id ?? null),
  });

  const { data: shiftsDataForRosterTable } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? undefined) : undefined
  );

  const {
    isPrevMonthOpen,
    setIsPrevMonthOpen,
    effectivePrevTailData,
    isFetchingPrevTail,
  } = useRosterPrevTailPanel({
    curDate,
    scheduleId: curScheduleInfo?.scheduleId,
    groupId: gid ?? "",
  });

  const { data: holidayDates = [] } = useHolidayDatesQuery(
    curDate?.year ?? undefined,
    curDate?.month ?? undefined
  );

  /* 모달 관련 */
  const [memo, setMemo] = useState<string>("");
  const uiMode = useRosterCreateUiStore((state) => state.mode);
  const setUiMode = useRosterCreateUiStore((state) => state.setMode);
  const resetUiMode = useRosterCreateUiStore((state) => state.resetMode);

  useEffect(
    () => () => {
      resetUiMode();
    },
    [resetUiMode]
  );

  // 로딩 상태 관리
  const [isDateChanging, setIsDateChanging] = useState<boolean>(false);
  const [isVersionChanging, setIsVersionChanging] = useState<boolean>(false);
  const [isDragEditing] = useState(false);

  const [fullNurseSchema, setFullNurseSchema] = useState<NurseResponse[]>([]);

  const [fullNurseSchemaAddPreceptor, setFullNurseSchemaAddPreceptor] =
    useState<FullNurseSchemaAddPreceptor>();

  const [availableShifts, setAvailableShifts] = useState<
    type.availableShifts[]
  >([]);

  /* 근무표 수정 관련 */
  const [selectedShift, setSelectedShift] =
    useState<type.Type_availableShifts | null>(null);

  const [currentTeamTableViewTab, setCurrentTeamTableViewTab] =
    useState<number>(0);

  /* 로딩 관련 */
  const [isLoadedPage, setIsLoadedPage] = useState<boolean>(false);

  const [currentConfigVer, setCurrentConfigVer] = useState<number | null>(null);

  const [currentShiftManage, setCurrentShiftManage] =
    useState<type.ShiftManageObject[]>();

  // defaultShiftCode를 useMemo로 직접 계산 (기존 useState + useEffect 제거)
  const defaultShiftCode = useMemo(() => {
    if (availableShifts.length === 0) return [];
    return availableShifts;
  }, [availableShifts]);

  const { lookupMaps: rosterShiftLookupMaps, catalog: rosterShiftCatalog } =
    useShiftLookupMaps(defaultShiftCode, shiftsDataForRosterTable ?? null);

  // ModalRosterWanted의 초기 탭 상태 관리
  const [wantedModalInitialTab, setWantedModalInitialTab] = useState<
    "view" | "settings"
  >("view");

  const [fullWanted, setFullWanted] = useState<type.WantedAll[]>([]);

  /* 버튼 클릭시 비활성화 관련 */
  const [isChangeDateButtonClick, setIsChangeDateButtonClick] =
    useState<boolean>(false);

  // 사이드 프로필 모달
  const [isSideProfileOpen, setIsSideProfileOpen] = useState<boolean>(false);
  const [activeNurseIdForSideProfile, setActiveNurseIdForSideProfile] =
    useState<string | null>(null);

  // TODO : 일단 임시로 이렇게 처리 / 원티드 조정판 열리면 부모페이지 스크롤 막음
  useEffect(() => {
    const mainContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement;

    if (!mainContainer) return;

    if (
      uiMode === "wanted" ||
      uiMode === "wantedSelector" ||
      uiMode === "wantedConfig"
    ) {
      mainContainer.style.overflow = "hidden";
    } else {
      mainContainer.style.overflow = "";
    }

    return () => {
      mainContainer.style.overflow = "";
    };
  }, [uiMode]);

  const {
    publishRoster,
    isPublishPending,
    unPublishRoster,
    isUnPublishPending,
  } = useRosterPublicationControls({
    curDate,
    scheduleId: curScheduleInfo?.scheduleId,
    selectedGroupId,
  });

  const scheduleDownload_excel = useRosterExcelDownload({
    curDate,
    scheduleId: curScheduleInfo?.scheduleId,
    isAdmin,
    selectedGroupId,
    currentTeamTableViewTab,
  });

  // 근무표 생성하기 - 커스텀으로 분리함
  const {
    mutateAsync: generateRosterMutate,
    isSuccess: isGenerateRosterSuccess,
    reset: resetGenerateRoster,
  } = useGenerateRosterMutation();
  const [blockingInfeasibility, setBlockingInfeasibility] =
    useState<Infeasibility | null>(null);

  useRosterCreateJobEffects({
    rosterStatus: rosterMade.status,
    rosterInfeasibility,
    curDate,
    selectedGroupId,
    isAdmin,
    queryClient,
    setCurScheduleInfo,
    setBlockingInfeasibility,
  });

  // 해결책 선택 재생성 시 재사용할 마지막 일반 생성 인자
  const lastGenArgsRef = useRef<{
    gradeStrategy: string;
    advancedInference: boolean;
    configIdOverride: number | null;
    config: RosterCreateConfigPayload | null;
  } | null>(null);

  const generateRoster = async ({
    gradeStrategy,
    advancedInference = false,
    configIdOverride = null,
    config = null,
    configOverride = null,
    treatmentIds = null,
    weekendOffRelease = null,
    monthlyLimitRelease = null,
    bannedWantedRelease = null,
    allowedShiftAdd = null,
    preserveInfeasibilityDuringRequest = false,
  }: {
    gradeStrategy: string;
    advancedInference?: boolean;
    configIdOverride?: number | null;
    config?: RosterCreateConfigPayload | null;
    /** 해결책 재생성: 고른 옵션의 config delta / treatment_ids (이번 생성에만 override) */
    configOverride?: Record<string, unknown> | null;
    treatmentIds?: string[] | null;
    /** per-nurse 주말휴무 해제 재생성 — 해당 nurse_id 들의 주말휴무를 해제(period)하고 재생성 */
    weekendOffRelease?: string[] | null;
    /** per-nurse 월 야간 한도 하향 재생성 — 해당 간호사 n_exact/n_max 하향 후 재생성 */
    monthlyLimitRelease?: Array<{
      nurse_id: string;
      field: "n_exact" | "n_max";
      value: number;
    }> | null;
    /** per-nurse 금지근무 해제 재생성 — 겹치는 날의 금지 셀을 해제(is_applied=False)하고 재생성 */
    bannedWantedRelease?: Array<{ nurse_id: string; days: number[] }> | null;
    /** per-nurse 근무유형 추가 재생성 — allowed_shifts 에 D/E 등을 합집합(period)하고 재생성 */
    allowedShiftAdd?: Array<{ nurse_id: string; add: string[] }> | null;
    preserveInfeasibilityDuringRequest?: boolean;
  }): Promise<GenerateRosterResponse | null | undefined> => {
    // 재생성(해결책 선택) 시 재사용하도록 마지막 생성 인자를 기억.
    //   ★ 해결카드 재생성도 기억해야 한다. 예전엔 카드 필드가 실리면 갱신을 건너뛰어
    //     base.config 가 최초 생성 시점에 멈춰 있었고, 그 탓에 카드를 연달아 누르면
    //     앞 카드의 config 변경이 유실됐다(2→6 으로 올린 뒤 개인 한도 카드를 누르면
    //     apply 가 없어 merged 가 base.config=2 로 돌아가 DB 가 2 로 회귀).
    //     카드는 누적되어야 하므로 조정된 config 를 그대로 다음 base 로 삼는다.
    lastGenArgsRef.current = {
      gradeStrategy,
      advancedInference,
      configIdOverride,
      config,
    };
    if (!preserveInfeasibilityDuringRequest) {
      setBlockingInfeasibility(null);
      setRosterInfeasibility(null);
    }

    if (
      shouldShowMockInfeasibilityFlow() &&
      !configOverride &&
      !treatmentIds &&
      !weekendOffRelease &&
      !monthlyLimitRelease &&
      !bannedWantedRelease &&
      !allowedShiftAdd
    ) {
      setBlockingInfeasibility(MOCK_BLOCKING_INFEASIBILITY);
      setRosterInfeasibility(MOCK_BLOCKING_INFEASIBILITY);
      return null;
    }

    try {
      const response = await generateRosterMutate({
        gradeStrategy,
        curDate,
        currentConfigVer,
        configIdOverride,
        config,
        isAdmin,
        selectedGroupId,
        advancedInference,
        configOverride,
        treatmentIds,
        weekendOffRelease,
        monthlyLimitRelease,
        bannedWantedRelease,
        allowedShiftAdd,
      });
      return response;
    } catch (error) {
      if (error instanceof RosterInfeasibleError) {
        setBlockingInfeasibility(error.infeasibility);
      }
      throw error;
    }
  };

  const regenerateWithResolution = async (opt: ResolutionOption) => {
    // 고른 옵션의 config delta(opt.apply) 를 재생성 payload 에 "명시적으로" 반영한다.
    // 초기 생성은 config(전체 객체)를 보내므로, 델타를 그 config 에 병합해서 payload 의
    // config 가 조정된 값(예: max_conseq_work=3)을 그대로 담게 한다(base+override 혼동 방지).
    // config 객체가 없으면(config_id 경로) config_override 로 전달. 비-컬럼 solver 키·
    // config_id 경로까지 커버하도록 config_override 도 함께 보낸다(중복은 무해).
    const base = lastGenArgsRef.current;
    const apply = (opt.apply as Record<string, unknown> | undefined) ?? null;
    const hasDelta = !!apply && Object.keys(apply).length > 0;
    const mergedConfig =
      base?.config && hasDelta
        ? ({ ...base.config, ...apply } as RosterCreateConfigPayload)
        : (base?.config ?? null);

    try {
      const response = await generateRoster({
        gradeStrategy: base?.gradeStrategy ?? "COMBINED",
        advancedInference: base?.advancedInference ?? false,
        configIdOverride: base?.configIdOverride ?? null,
        config: mergedConfig,
        configOverride: apply,
        treatmentIds: opt.treatment_ids ?? null,
        weekendOffRelease: opt.weekend_off_release ?? null,
        monthlyLimitRelease: opt.monthly_limit_release ?? null,
        bannedWantedRelease: opt.banned_wanted_release ?? null,
        allowedShiftAdd: opt.allowed_shift_add ?? null,
        preserveInfeasibilityDuringRequest: true,
      });
      if (!response) return;

      setBlockingInfeasibility(null);
      setRosterInfeasibility(null);
      if (SHOULD_USE_ASYNC_ROSTER_GENERATION) {
        useStore.getState().startPolling();
      } else {
        useStore.getState().resetRosterMadeState();
      }
    } catch (error) {
      if (!(error instanceof RosterInfeasibleError)) {
        toast.error(
          error instanceof Error
            ? error.message
            : "근무표 재생성 요청에 실패했습니다."
        );
      }
    }
  };

  useEffect(() => {
    if (isGenerateRosterSuccess) {
      const timer = setTimeout(() => {
        resetGenerateRoster();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isGenerateRosterSuccess, resetGenerateRoster]);

  // 버전 리스트는 useRosterVersions로 가져오기 (중복 호출 방지)
  const { data: versionList } = useRosterVersions({
    groupId: selectedGroupId ?? null,
  });

  // 현재 그룹의 버전 목록에 존재하는 schedule_id일 때만 스케줄 조회
  // 삭제된 scheduleId는 절대 요청하지 않도록 체크
  const { deletedScheduleIds } = useScheduleStore();
  const isValidScheduleForCurrentGroup = useMemo(() => {
    if (!curScheduleInfo?.scheduleId || !versionList) return false;
    // 삭제된 scheduleId는 요청하지 않음
    if (deletedScheduleIds.has(curScheduleInfo.scheduleId)) return false;
    return versionList.some(
      (v) => v.schedule_id === curScheduleInfo.scheduleId
    );
  }, [curScheduleInfo?.scheduleId, versionList, deletedScheduleIds]);

  // Group (스토어·그룹 목록 동기화; 반환값은 이 페이지에서 미사용)
  useInitializeGroup({
    loginUser,
    isAdmin,
  });

  // StrictMode 중복 호출 및 ADM의 selectedGroupId 미준비 상태 가드
  useRosterInitializer({ groupId: gid ?? null });

  // 캐싱 데이터
  const { data, isLoading: isResourceLoading } = useRosterResources({
    groupId: gid ?? "",
  });

  // 스케줄 데이터는 useQuery로 캐싱
  const { data: rosterData, isLoading } = useRosterSchedule({
    scheduleId: curScheduleInfo?.scheduleId,
    groupId: gid ?? "",
    enabled: isValidScheduleForCurrentGroup,
  });

  // === 커스텀 훅: useRosterEditing ===
  const {
    copyNurses,
    setCopyNurses,
    isRosterEmpty,
    modifiedCells,
    setModifiedCells,
    addModifiedCell,
  } = useRosterEditing({
    rosterData,
    fullNurseSchema,
    fullNurseSchemaAddPreceptor,
    curDate,
    rosterContextKey: `${gid ?? ""}:${curScheduleInfo?.scheduleId ?? ""}`,
  });

  // useEmergencyReplacement에서 nursesIndex 조회를 위해 최신 copyNurses 유지
  const copyNursesRef = useRef(copyNurses);
  copyNursesRef.current = copyNurses;

  const {
    emergencySlotPick,
    setEmergencySlotPick,
    emergencyBulkPeriodPick,
    setEmergencyBulkPeriodPick,
    emergencySingleHasResult,
    setEmergencySingleHasResult,
    emergencyBulkSelectedSteps,
    setEmergencyBulkSelectedSteps,
    emergencyBulkHasResult,
    setEmergencyBulkHasResult,
    bulkResultResetRef,
    setEmergencyReplacementPeriodMode,
    handleEmergencyReplacementCellPick,
    emergencyReplacementCandidateNurseId,
    setEmergencyReplacementCandidateNurseId,
    emergencyPostApplyHighlight,
    handleEmergencyModalOpenChange,
    openEmergencyModalFromButton,
    applyEmergencyReplacement,
    resetEmergencyOnDateChange,
  } = useEmergencyReplacement({
    setCopyNurses,
    defaultShiftCode,
    addModifiedCell,
  });

  // === 커스텀 훅: useRosterHistory ===
  const {
    handleUndo,
    handleRedo,
    modDayShift,
    deleteAllShift,
    canUndo,
    canRedo,
    clearHistory,
  } = useRosterHistory({
    copyNurses,
    setCopyNurses,
    addModifiedCell,
    modifiedCells,
    setModifiedCells,
  });

  // === 커스텀 훅: useManpower ===장
  const manpowerOid = isAdmin ? selectedOfficeId : loginUser?.office_id;
  const manpowerGid = isAdmin ? selectedGroupId : loginUser?.group_id;
  const {
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    manpowerOid: resolvedManpowerOid,
    manpowerGid: resolvedManpowerGid,
    year: manpowerYear,
    month: manpowerMonth,
  } = useManpower({
    manpowerOid: manpowerOid ?? undefined,
    manpowerGid: manpowerGid ?? undefined,
    year: curDate?.year ?? undefined,
    month: curDate?.month ?? undefined,
    isAdmin,
    selectedOfficeId,
    selectedGroupId,
    loginUser,
  });

  // 위반사항 토글 상태 (전역 store)
  const {
    showWantedViolations,
    toggleWantedViolations,
    showManpowerViolations,
    toggleManpowerViolations,
    showConfigViolations,
    toggleConfigViolations,
  } = useStore();

  useRosterCreateResourceSync({
    data,
    setFullNurseSchema,
    setFullNurseSchemaAddPreceptor,
    setAvailableShifts,
    setCurrentConfigVer,
    setCurrentShiftManage,
    setFullWanted,
    setIsLoadedPage,
  });

  useRosterVersionSelectionEffects({
    curDate,
    curScheduleInfo,
    versionList,
    rosterData,
    isLoading,
    isVersionChanging,
    clearHistory,
    setUiMode,
    setCurScheduleInfo,
    setIsDateChanging,
    setIsVersionChanging,
    setIsChangeDateButtonClick,
    resetEmergencyOnDateChange,
    showWantedViolations,
    showManpowerViolations,
    showConfigViolations,
    toggleWantedViolations,
    toggleManpowerViolations,
    toggleConfigViolations,
  });

  // rosterData의 memo 업데이트
  useEffect(() => {
    if (rosterData?.memo !== undefined) {
      setMemo(rosterData.memo ?? "");
    }
  }, [rosterData?.memo]);

  const { data: wantedStatus } = useWantedStatusQuery({
    year: curDate?.year ?? 0,
    month: curDate?.month ?? 0,
    groupId: gid ?? null,
  });

  // 원티드 반영 설정 데이터 가져오기 (버전이 있을 때만)
  //   ★ 토글과 무관하게 받아온다 — 요약 바가 "금지가 몇 건 걸려 있는지"를 토글 켜기
  //     전에 알려야 하기 때문. 쿼리 키가 같아서 토글을 켜면 캐시에서 바로 뜬다.
  const {
    data: wantedAdjustmentData,
    isLoading: isWantedAdjustmentFetching,
  } = useWantedAdjustmentQuery(
    curDate?.year,
    curDate?.month,
    !!curScheduleInfo?.scheduleId
  );
  // 로딩 게이트는 토글이 켜졌을 때만. 안 그러면 요약 바 때문에 항상 fetch 하므로
  //   화면에 들어올 때마다 오버레이가 뜨고 버튼이 잠긴다.
  const isWantedAdjustmentLoading =
    isWantedAdjustmentFetching && showWantedViolations;

  const currentRosterGroupId = isAdmin
    ? (selectedGroupId ?? null)
    : (loginUser?.group_id ?? null);
  const { data: monthMembers } = useGroupMembers(
    currentRosterGroupId,
    curDate?.year ?? undefined,
    curDate?.month ?? undefined
  );
  const memberStatusMap = useMemo<
    ReadonlyMap<string, GroupMemberInMonth> | undefined
  >(() => {
    if (!monthMembers) return undefined;
    return new Map(
      monthMembers.members.map((member) => [member.nurse_id, member] as const)
    );
  }, [monthMembers]);
  // copyNurses에 isAppliedMap 및 wantedEntriesMap 매핑
  //   enabled=토글 — isAppliedMap 이 얹히면 useRosterCreateDerivedNurses 가 껐음 날짜의
  //   셀을 null 로 만들어 검증 결과가 바뀐다. 표시할 때만 얹어야 한다.
  const copyNursesWithWanted = useRosterWantedOverlay({
    copyNurses,
    wantedAdjustmentData,
    year: curDate?.year,
    month: curDate?.month,
    enabled: showWantedViolations,
  });

  // 토글과 무관한 그룹 전체 합계 (요약 바용). 오버레이와 분리돼 검증에 영향 없음.
  const wantedComplianceTotals = useWantedComplianceTotals({
    copyNurses,
    wantedAdjustmentData,
    year: curDate?.year,
    month: curDate?.month,
    shiftLookupMaps: rosterShiftLookupMaps,
  });
  // 프리셉티 표시 = nurse_preceptee_period(SSOT) 월 스코프 조회 (assignment 안 봄).
  const { data: precepteePeriodsData } = usePrecepteePeriodsQuery({
    groupId: gid,
    year: curDate?.year,
    month: curDate?.month,
  });
  const visibleCopyNursesWithWanted = useRosterCreateVisibleNurses({
    copyNurses: copyNursesWithWanted,
    memberStatusMap,
    profileNurses: fullNurseSchemaAddPreceptor,
    groupId: currentRosterGroupId,
    year: curDate?.year,
    month: curDate?.month,
  });
  const displayNurses = useMemo(() => {
    const periods = precepteePeriodsData?.items ?? [];
    if (periods.length === 0) {
      return visibleCopyNursesWithWanted.map((n) =>
        n.preceptor ? { ...n, preceptor: null } : n
      );
    }

    const nurseNameById = new Map(
      visibleCopyNursesWithWanted.map((n) => [String(n.nurse_id), n.name])
    );
    const preceptorToPreceptee = new Map<string, string[]>();

    for (const period of periods) {
      const precepteeId = String(period.nurse_id);
      const preceptorId = String(period.preceptor_id);
      if (!precepteeId || !preceptorId) continue;
      const precepteeIds = preceptorToPreceptee.get(preceptorId) ?? [];
      if (!precepteeIds.includes(precepteeId)) {
        precepteeIds.push(precepteeId);
      }
      preceptorToPreceptee.set(preceptorId, precepteeIds);
    }

    const groups = Array.from(
      preceptorToPreceptee.entries(),
      ([preceptor, preceptee]) => ({
        preceptor,
        preceptee: [...preceptee].sort((a, b) => a.localeCompare(b)),
      })
    ).sort((a, b) => a.preceptor.localeCompare(b.preceptor));

    return visibleCopyNursesWithWanted.map((n) => {
      const nurseId = String(n.nurse_id);
      const groupIndex = groups.findIndex(
        (group) =>
          group.preceptor === nurseId || group.preceptee.includes(nurseId)
      );

      if (groupIndex === -1) {
        return n.preceptor ? { ...n, preceptor: null } : n;
      }

      const group = groups[groupIndex];
      return {
        ...n,
        preceptor: {
          role:
            group.preceptor === nurseId
              ? ("preceptor" as const)
              : ("preceptee" as const),
          group,
          groupIndex,
          color: PRECEPTOR_MARK_COLORS[groupIndex] ?? "204, 204, 204",
          or: [nurseNameById.get(group.preceptor) ?? group.preceptor],
          ee: group.preceptee.map(
            (precepteeId) => nurseNameById.get(precepteeId) ?? precepteeId
          ),
        },
      };
    });
  }, [precepteePeriodsData?.items, visibleCopyNursesWithWanted]);

  const { data: serverConfig } = useRosterConfig();
  const { isUseMid, isUseMidSaving, debouncedSetUseMid } =
    useRosterUseMidControl({
      serverConfig,
      isAdmin,
      selectedGroupId,
      resolvedManpowerOid,
      resolvedManpowerGid,
      manpowerYear,
      manpowerMonth,
    });

  // 프리셉티 카운트 제외를 캐시가 아니라 period(대상월 day 집합)으로 판정(종료 후 자동 카운트).
  // precepteePeriodsData 는 위(visibleNurses 앞)에서 이미 조회됨.
  const precepteeDaysByNurseId = useMemo(
    () =>
      getPrecepteeDaysByNurseId(
        precepteePeriodsData?.items,
        curDate?.year,
        curDate?.month
      ),
    [precepteePeriodsData, curDate?.year, curDate?.month]
  );
  const hasPrecepteeStatsTarget = precepteeDaysByNurseId.size > 0;
  const { validationCopyNurses, statsNurses } = useRosterCreateDerivedNurses({
    copyNursesWithWanted: visibleCopyNursesWithWanted,
    includePrecepteeInValidationCount: true,
    includePrecepteeInStatsCount: precepteeStatsIncluded,
    precepteeDaysByNurseId,
  });

  const {
    showPerShiftStats,
    setShowPerShiftStats,
    statsShiftCode,
    rosterCreateShiftLegend,
  } = useRosterCreateDisplayShifts({
    copyNursesWithWanted: visibleCopyNursesWithWanted,
    defaultShiftCode,
    isUseMid,
    year: curDate?.year,
    month: curDate?.month,
    scheduleId: curScheduleInfo?.scheduleId,
  });

  // 근무표가 비어있는지 확인 (모든 스케줄이 '-' 이거나 없는 경우) - useRosterEditing에서 가져옴

  // 발행된 근무표 상태
  const isPublished = useMemo(
    () =>
      versionList?.find((v) => v.schedule_id === curScheduleInfo?.scheduleId)
        ?.status === "issued",
    [versionList, curScheduleInfo?.scheduleId]
  );

  // modifiedCells context value 메모이제이션
  const modifiedCellsValue = useMemo(
    () => ({ modifiedCells }),
    [modifiedCells]
  );

  // currentScheduleInfo 객체 메모이제이션 (여러 곳에서 사용)
  const currentScheduleInfoMemo = useMemo(
    () => ({
      year: curDate?.year ?? 0,
      month: curDate?.month ?? 0,
      version: curScheduleInfo?.version ?? 0,
    }),
    [curDate?.year, curDate?.month, curScheduleInfo?.version]
  );
  const { moveRosterNurseRow, sortRosterNurseRows } =
    useRosterCreateNurseReorder({
      copyNurses,
      setCopyNurses,
      copyNursesRef,
      sequenceSourceRows: fullNurseSchemaAddPreceptor ?? fullNurseSchema,
      groupId: currentRosterGroupId,
    });

  // 검증 훅 (근무표 만들기 전용) — 결과를 RosterMadeTable에 prop으로 전달
  const {
    validateList: rawValidateListFromHook,
    cellViolationMap: rosterCellViolationMap,
    isValidating: isRosterValidating,
    phase: rosterValidationPhase,
    validate: triggerRosterValidation,
  } = useRosterValidation({
    currentScheduleInfo: currentScheduleInfoMemo,
    currentScheduleID: curScheduleInfo?.scheduleId,
    copyNurses: validationCopyNurses as Parameters<
      typeof useRosterValidation
    >[0]["copyNurses"],
    isDragEditing,
    manPower_monthly,
    manPower_daily,
    useMaxManpower,
    enableMaxManpowerViolation: ENABLE_MAX_MANPOWER_VIOLATION,
    year: curDate?.year ?? 0,
    month: curDate?.month ?? 0,
    serverConfig: serverConfig ?? null,
    defaultShiftCode,
    shiftsData: shiftsDataForRosterTable ?? [],
    shiftCatalog: rosterShiftCatalog,
    disableManpowerValidation: false,
    prevTailData: effectivePrevTailData,
    currentGroupId: currentRosterGroupId,
    memberStatusMap,
  });

  useEffect(() => {
    if (!isDragEditing) triggerRosterValidation();
  }, [
    validationCopyNurses,
    curScheduleInfo?.scheduleId,
    isDragEditing,
    triggerRosterValidation,
  ]);

  // 버전 변경 시 위반사항 토글 리셋
  const prevVersionRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const ver = currentScheduleInfoMemo.version;
    if (
      prevVersionRef.current !== undefined &&
      prevVersionRef.current !== ver
    ) {
      if (showWantedViolations) toggleWantedViolations();
      if (!showManpowerViolations) toggleManpowerViolations();
      if (!showConfigViolations) toggleConfigViolations();
    }
    prevVersionRef.current = ver;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScheduleInfoMemo.version]);

  // selectIdForSideprofile 함수 메모이제이션
  const handleSelectIdForSideProfile = useCallback(
    (bool: boolean, nurseID: string) => {
      setActiveNurseIdForSideProfile(nurseID);
      setIsSideProfileOpen(bool);
    },
    []
  );

  // fetchWantedStatus 함수 메모이제이션
  const handleFetchWantedStatus = useCallback(
    (year: number, month: number) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.status(year, month, gid ?? ""),
      });
    },
    [queryClient, gid]
  );

  // ModalRosterWanted onClose 함수 메모이제이션
  const handleModalRosterWantedClose = useCallback(() => {
    // ModalRosterWanted를 닫을 때 WantedSelectorModal을 다시 열기
    setUiMode("wantedSelector");
  }, [setUiMode]);

  if (!isLoadedPage || isResourceLoading)
    return (
      <div className="rosterCreating">
        근무표를 불러오고 있습니다.
        <LoadingDots />
      </div>
    );

  const isGenerateRequesting =
    rosterMade.status === "QUEUED" || rosterMade.status === "RUNNING";
  const shouldShowWantedAdjustmentLoadingOverlay =
    isWantedAdjustmentLoading && uiMode !== "wanted" && !isGenerateRequesting;

  return (
    <ModifiedCellsProvider value={modifiedCellsValue}>
      <div
        className={cn(
          "font-pretendard   w-full",
          rosterMade.status && "overflow-hidden!"
        )}
      >
        <div className=" flex flex-col items-center gap-10  ">
          <div
            className={` flex flex-col items-center w-full  ${uiMode === "edit" ? "us-n" : ""}`}
          >
            <div className="w-full px-5 sticky justify-center   bg-white  top-0 z-20  flex  items-end">
              <div className=" pt-5 ">
                <div className=" flex flex-col">
                  <ButtonGroup
                    editModeButtonRef={rosterModifyButtonRef}
                    memo={memo}
                    copyNurse={visibleCopyNursesWithWanted}
                    generateRoster={generateRoster}
                    isPublishPending={isPublishPending}
                    publishRoster={publishRoster}
                    isPublished={!!isPublished}
                    unPublishRoster={unPublishRoster}
                    isUnPublishPending={isUnPublishPending}
                    setWantedModalInitialTab={setWantedModalInitialTab}
                    scheduleDownload_excel={scheduleDownload_excel}
                    isRosterCreating={
                      rosterMade.status === "QUEUED" ||
                      rosterMade.status === "RUNNING"
                    }
                    isChangeDateButtonClick={isChangeDateButtonClick}
                    setIsChangeDateButtonClick={setIsChangeDateButtonClick}
                    currentTeamTableViewTab={currentTeamTableViewTab}
                    selectedGroupId={selectedGroupId}
                    isRosterEmpty={isRosterEmpty}
                    isWantedAdjustmentLoading={isWantedAdjustmentLoading} // 원티드 반영사항 로딩 중일 때 버튼 비활성화
                    clearHistory={clearHistory}
                    emergencySlotPick={emergencySlotPick}
                    setEmergencySlotPick={setEmergencySlotPick}
                    emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                    setEmergencyBulkPeriodPick={setEmergencyBulkPeriodPick}
                    onEmergencyBulkPathStepsChange={
                      setEmergencyBulkSelectedSteps
                    }
                    onEmergencySingleResultChange={setEmergencySingleHasResult}
                    onEmergencyBulkResultChange={setEmergencyBulkHasResult}
                    emergencyBulkResultResetRef={bulkResultResetRef}
                    onEmergencyReplacementPeriodModeChange={
                      setEmergencyReplacementPeriodMode
                    }
                    setEmergencyReplacementCandidateNurseId={
                      setEmergencyReplacementCandidateNurseId
                    }
                    onApplyEmergencyReplacement={applyEmergencyReplacement}
                    availableShifts={availableShifts}
                    groupId={resolvedManpowerGid ?? ""}
                    onEmergencyModalOpenChange={handleEmergencyModalOpenChange}
                    openEmergencyModalFromButton={openEmergencyModalFromButton}
                  />

                  <div
                    className={cn(
                      "animate-in fade-in-0 duration-300 transition-opacity relative mb-4"
                    )}
                  >
                    <RosterMadeTable
                      defaultShiftCode={statsShiftCode}
                      shiftLookupMaps={rosterShiftLookupMaps}
                      manPower_monthly={manPower_monthly}
                      useMaxManpower={useMaxManpower}
                      useMid={isUseMid !== false}
                      isUseMidSaving={isUseMidSaving}
                      onUseMidChange={debouncedSetUseMid}
                      toolbarControls={
                        <RosterViewSettings
                          currentTeamTableViewTab={currentTeamTableViewTab}
                          setCurrentTeamTableViewTab={
                            setCurrentTeamTableViewTab
                          }
                          isWantedAdjustmentLoading={isWantedAdjustmentLoading}
                          isPrevMonthOpen={isPrevMonthOpen}
                          setIsPrevMonthOpen={setIsPrevMonthOpen}
                          isFetchingPrevTail={isFetchingPrevTail}
                          curDate={curDate}
                          variant="inline"
                          wantedCompliance={wantedComplianceTotals}
                        />
                      }
                      year={curDate?.year ?? 0}
                      month={curDate?.month ?? 0}
                      showManpowerButton={true}
                      memo={memo}
                      setMemo={setMemo}
                      isEditableMemo={true}
                      copyNurses={statsNurses}
                      precepteeStatsIncluded={precepteeStatsIncluded}
                      onPrecepteeStatsIncludedChange={setPrecepteeStatsIncluded}
                      hasPrecepteeStatsTarget={hasPrecepteeStatsTarget}
                      hasPrevTail={!!effectivePrevTailData}
                      hideViolationToggleRow
                      hideViolationFloating
                      rawValidateList={rawValidateListFromHook}
                      isValidating={isRosterValidating}
                      phase={rosterValidationPhase}
                      violationState={{
                        showManpowerViolations,
                        showConfigViolations,
                        showWantedViolations,
                        toggleManpowerViolations,
                        toggleConfigViolations,
                        toggleWantedViolations,
                      }}
                      role={
                        isAdmin ? "admin" : isHeadNurse ? "head_nurse" : "nurse"
                      }
                      showPrecepteeBadge={
                        hasPrecepteeStatsTarget && !precepteeStatsIncluded
                      }
                    />
                  </div>
                  <RosterViolationFloatingControls
                    rawValidateList={rawValidateListFromHook}
                    cellViolationMap={rosterCellViolationMap}
                    isValidating={isRosterValidating}
                    phase={rosterValidationPhase}
                    violationState={{
                      showManpowerViolations,
                      showConfigViolations,
                      showWantedViolations,
                      toggleManpowerViolations,
                      toggleConfigViolations,
                      toggleWantedViolations,
                    }}
                  />
                </div>
                <RosterCreateTableHeader
                  defaultShiftCode={defaultShiftCode}
                  copyNurses={visibleCopyNursesWithWanted}
                  setCopyNurses={setCopyNurses}
                  onSortedRows={sortRosterNurseRows}
                  currentYear={curDate?.year ?? 0}
                  currentMonth={curDate?.month ?? 0}
                  holidayDates={holidayDates}
                  prevTailData={effectivePrevTailData}
                  showPerShiftStats={showPerShiftStats}
                  setShowPerShiftStats={setShowPerShiftStats}
                  rosterCreateShiftLegend={rosterCreateShiftLegend}
                />
              </div>
            </div>

            {/* Roster Summary */}
            <div className="relative pb-30 px-5">
              <ContainerLoadingOverlay
                isOpen={
                  isLoading ||
                  isDateChanging ||
                  isVersionChanging ||
                  shouldShowWantedAdjustmentLoadingOverlay
                }
                title={
                  isLoading || isDateChanging || isVersionChanging ? (
                    <>
                      {curDate?.year}년 {curDate?.month}월
                      <span className="text-sig">
                        {curScheduleInfo?.version
                          ? ` ver  ${curScheduleInfo?.version}`
                          : ""}
                      </span>{" "}
                      근무표를 가져오는 중입니다.
                    </>
                  ) : (
                    <>
                      {curDate?.year}년 {curDate?.month}월 원티드 반영사항을
                      가져오는 중입니다.
                    </>
                  )
                }
              />

              <div
                className={cn(
                  "animate-in fade-in-0 duration-300 transition-opacity relative"
                )}
              >
                {/* 개별 근무표 보기임 */}
                {currentTeamTableViewTab === 0 && (
                  <div className="relative">
                    {/* 근무 반영 로직 */}
                    <TableBody
                      mode="CREATE"
                      copyNurses={displayNurses}
                      setCopyNurses={(
                        value: Parameters<typeof setCopyNurses>[0]
                      ) => setCopyNurses(value)}
                      availableShifts={availableShifts}
                      isEditMode={uiMode === "edit"}
                      selectedShift={selectedShift}
                      modDayShift={modDayShift}
                      enableRowDragSort={false}
                      enableRowSequenceReorder
                      onMoveNurseRow={moveRosterNurseRow}
                      saveNurseSequence={async () => {}}
                      currentScheduleInfo={currentScheduleInfoMemo}
                      currentScheduleID={curScheduleInfo?.scheduleId}
                      isDragEditing={isDragEditing}
                      selectedGroupId={selectedGroupId}
                      manPower_monthly={manPower_monthly}
                      manPower_daily={manPower_daily}
                      useMaxManpower={useMaxManpower}
                      enableMaxManpowerViolation={ENABLE_MAX_MANPOWER_VIOLATION}
                      year={curDate?.year ?? 0}
                      month={curDate?.month ?? 0}
                      selectIdForSideprofile={handleSelectIdForSideProfile}
                      prevTailData={effectivePrevTailData}
                      isEmergencyPickMode={
                        uiMode === "emergency" && currentTeamTableViewTab === 0
                      }
                      onEmergencySlotPick={(pick) =>
                        handleEmergencyReplacementCellPick(
                          pick,
                          emergencyBulkHasResult,
                          emergencySingleHasResult
                        )
                      }
                      emergencySlotPick={emergencySlotPick}
                      isEmergencyBulkPickMode={false}
                      emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                      emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
                      emergencyReplacementCandidateNurseId={
                        emergencyReplacementCandidateNurseId
                      }
                      emergencyPostApplyHighlight={emergencyPostApplyHighlight}
                      showPerShiftStats={showPerShiftStats}
                      cellViolationMap={rosterCellViolationMap}
                      schedulePayloadYear={rosterData?.year}
                      schedulePayloadMonth={rosterData?.month}
                      currentGroupId={currentRosterGroupId}
                    />
                  </div>
                )}

                {/* 팀별 근무표 보기임 */}
                {currentTeamTableViewTab === 1 && (
                  <Roster_create_Tab2
                    copyNurses={displayNurses}
                    availableShifts={availableShifts}
                    isEditMode={uiMode === "edit"}
                    selectedShift={selectedShift}
                    modDayShift={modDayShift}
                    enableRowSequenceReorder
                    onMoveNurseRow={moveRosterNurseRow}
                    prevTailData={effectivePrevTailData}
                    showPerShiftStats={showPerShiftStats}
                    cellViolationMap={rosterCellViolationMap}
                    selectIdForSideprofile={handleSelectIdForSideProfile}
                    scheduleYear={curDate?.year ?? 0}
                    scheduleMonth={curDate?.month ?? 0}
                    schedulePayloadYear={rosterData?.year}
                    schedulePayloadMonth={rosterData?.month}
                    currentGroupId={currentRosterGroupId}
                    isEmergencyPickMode={
                      uiMode === "emergency" && currentTeamTableViewTab === 1
                    }
                    onEmergencySlotPick={(pick) =>
                      handleEmergencyReplacementCellPick(
                        pick,
                        emergencyBulkHasResult,
                        emergencySingleHasResult
                      )
                    }
                    emergencySlotPick={emergencySlotPick}
                    isEmergencyBulkPickMode={false}
                    emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                    emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
                    emergencyReplacementCandidateNurseId={
                      emergencyReplacementCandidateNurseId
                    }
                    emergencyPostApplyHighlight={emergencyPostApplyHighlight}
                  />
                )}
              </div>
            </div>
          </div>

          {uiMode === "edit" && (
            <ModalRosterModify
              modalClose={(open) => {
                if (!open) setUiMode("none");
              }}
              targetRef={rosterModifyButtonRef as RefObject<HTMLElement | null>}
              availableShifts={availableShifts}
              selectedShift={selectedShift}
              setSelectedShift={setSelectedShift}
              deleteAllShift={deleteAllShift}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={canUndo}
              canRedo={canRedo}
            />
          )}

          {uiMode === "deadline" && wantedStatus && (
            <SetDeadLine
              setIsOpendDeadlineModal={(open) =>
                setUiMode(open ? "deadline" : "none")
              }
              currentScheduleInfo={currentScheduleInfoMemo}
              wantedStatus={wantedStatus}
              fetchWantedStatus={handleFetchWantedStatus}
              setIsOpendModalSubmittedWantedlistModal={(open) =>
                setUiMode(open ? "wanted" : "none")
              }
              targetRef={wantedButtonRef}
              clearHistory={clearHistory}
            />
          )}

          {uiMode === "wanted" && (
            <ModalRosterWanted
              defaultShiftCode={defaultShiftCode}
              currentScheduleInfo={currentScheduleInfoMemo}
              holidayDates={holidayDates}
              shiftInfo={availableShifts}
              nurses={
                (fullNurseSchemaAddPreceptor ||
                  fullNurseSchema) as NurseResponse[]
              }
              onClose={handleModalRosterWantedClose}
              isViewOnlyOff={false}
              initialTab={wantedModalInitialTab}
              showManpowerButton={false} // 원티드 모달: 인력 설정 버튼 숨김(uiMode 충돌 방지). 최소인원 숫자는 manPower_monthly
              manPower_monthly={manPower_monthly}
              manPower_daily={manPower_daily}
              memo={memo}
              setMemo={setMemo}
              isEditableMemo={false}
              currentScheduleID={curScheduleInfo?.scheduleId}
              prevTailData={effectivePrevTailData}
            />
          )}

          {uiMode === "wantedConfig" && (
            <ModalWantedConfig onClose={handleModalRosterWantedClose} />
          )}

          {uiMode === "manpower" && (
            <ModalManpower
              onClose={() => setUiMode("none")}
              availableShifts={availableShifts}
              defaultShiftCode={defaultShiftCode}
              holidayDates={holidayDates}
              officeId={resolvedManpowerOid ?? ""}
              groupId={resolvedManpowerGid ?? ""}
              year={manpowerYear ?? 0}
              month={manpowerMonth ?? 0}
              initialMonthly={manPower_monthly ?? ({} as type.ManPowerMonthly)}
              initialDaily={manPower_daily ?? ({} as type.ManPowerDaily)}
              initialMaxEnabled={useMaxManpower}
              currentShiftManage={currentShiftManage ?? []}
              totalNursesCount={visibleCopyNursesWithWanted.length}
              isUseMidSaving={isUseMidSaving}
              onUseMidChange={debouncedSetUseMid}
              useMidOverride={isUseMid}
            />
          )}

          {/* OFF 요청 현황 모달 */}
          {uiMode === "offRequest" && curDate?.year && curDate?.month && (
            <ModalOffRequestList
              defaultShiftCode={defaultShiftCode}
              currentScheduleInfo={{
                year: currentScheduleInfoMemo.year,
                month: currentScheduleInfoMemo.month,
              }}
              holidayDates={holidayDates}
              nurses={fullNurseSchema}
              shiftInfo={availableShifts}
              fullWanted={fullWanted}
              onClose={() => setUiMode("none")}
            />
          )}

          {/* 사이드 프로필 모달 */}
          {isSideProfileOpen && (
            <NurseEditSideProfile
              onlyView={true}
              isOpen={isSideProfileOpen}
              onClose={() => {
                setIsSideProfileOpen(false);
                setActiveNurseIdForSideProfile(null);
              }}
              selectedGroupId={selectedGroupId}
              isAdmin={isAdmin}
              nurseId={activeNurseIdForSideProfile}
              allNurses={fullNurseSchema}
              year={curDate?.year ?? undefined}
              month={curDate?.month ?? undefined}
            />
          )}
        </div>
        <RosterCreateBlockingInfeasibilityAlert
          infeasibility={blockingInfeasibility}
          onClose={() => {
            // 그냥 닫기 = 이 실패 데이터 소멸(재생성 안 함).
            setBlockingInfeasibility(null);
            setRosterInfeasibility(null);
          }}
          onSelectResolution={regenerateWithResolution}
        />
        {/* 근무표 생성 상태 분리 */}

        <RosterIndicater
          resetGenerateRoster={resetGenerateRoster}
          generatedVersion={curScheduleInfo?.version}
        />
      </div>
    </ModifiedCellsProvider>
  );
}
export default Roster_create;
